import { useEffect, useState } from 'react'
import { intervalToDuration } from 'date-fns'

type CountdownFormat = 'clock' | 'full'

function formatCountdown(expireAt: string, format: CountdownFormat): string {
	const now = Date.now()
	const expiration = new Date(expireAt)
	const remainingSeconds = Math.floor((expiration.getTime() - now) / 1000)

	if (remainingSeconds <= 0) {
		return format === 'full' ? 'Expired' : '00:00:00'
	}

	if (format === 'full' && remainingSeconds >= 72 * 3600) {
		const { years = 0, months = 0, days = 0 } = intervalToDuration({ start: now, end: expiration })

		return [years > 0 ? `${years}y` : null, months > 0 ? `${months}m` : null, days > 0 ? `${days}d` : null]
			.filter((part): part is string => part !== null)
			.join(' ')
	}

	const hours = Math.floor(remainingSeconds / 3600)
	const minutes = Math.floor((remainingSeconds % 3600) / 60)
	const seconds = remainingSeconds % 60

	return [hours, minutes, seconds].map((part) => String(part).padStart(2, '0')).join(':')
}

export function useCountdown(expireAt: string, format: CountdownFormat = 'clock'): string {
	const [remaining, setRemaining] = useState(() => formatCountdown(expireAt, format))

	useEffect(() => {
		const tick = () => {
			setRemaining(formatCountdown(expireAt, format))
		}

		tick()
		const intervalId = window.setInterval(tick, 1000)

		return () => window.clearInterval(intervalId)
	}, [expireAt, format])

	return remaining
}
